/**
 * DashboardPage — główny ekran (Faza 3).
 * Wallet selector → Search → MonthNavigator (z summary) → TransactionList.
 * Pull-to-refresh w Layout (TODO Faza 3.5).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { WalletScroller } from '../components/dashboard/WalletScroller';
import { MonthNavigator } from '../components/dashboard/MonthNavigator';
import { SearchBar } from '../components/dashboard/SearchBar';
import { TransactionList } from '../components/dashboard/TransactionList';
import { useWalletsStore } from '../features/wallets/walletsStore';
import { useTransactionsStore } from '../features/transactions/transactionsStore';
import { useTxModal } from '../features/transactions/txModalStore';
import { Icons } from '../components/Icons';
import {
  filterTransactions,
  computeMonthSummary,
} from '../features/transactions/selectors';
import { getMonthBounds } from '../lib/utils';

export function DashboardPage() {
  const transactions = useTransactionsStore((s) => s.transactions);
  const loading = useTransactionsStore((s) => s.loading);
  const wallets = useWalletsStore((s) => s.wallets);
  const selectedWalletId = useWalletsStore((s) => s.selectedWalletId);

  const [month, setMonth] = useState<Date>(() => new Date());
  const [searchQuery, setSearchQuery] = useState('');

  // Pierwszy załadunek: jeśli bieżący miesiąc pusty ale są wcześniejsze tx,
  // skacz do miesiąca ostatniej transakcji żeby user widział dane od razu.
  // Robimy tylko raz, potem user samodzielnie nawiguje.
  const autoMonthSet = useRef(false);
  useEffect(() => {
    if (autoMonthSet.current) return;
    if (transactions.length === 0) return;
    const now = new Date();
    const hasCurrentMonth = transactions.some(
      (t) => t.date.getFullYear() === now.getFullYear() && t.date.getMonth() === now.getMonth(),
    );
    if (hasCurrentMonth) {
      autoMonthSet.current = true;
      return;
    }
    const latest = transactions.reduce((acc, t) => (t.date > acc ? t.date : acc), transactions[0]!.date);
    setMonth(new Date(latest.getFullYear(), latest.getMonth(), 1));
    autoMonthSet.current = true;
  }, [transactions]);

  const selectedType = wallets.find((w) => w.id === selectedWalletId)?.type;
  // Wspólny portfel (shared) lub pseudo-wallet 'all' = agregator → bez filtra walletId.
  // Wcześniej 'all' (default selection) traktowany był jako prawdziwy walletId
  // → filterTransactions szukało tx.walletId === 'all' = nigdy nie pasowało.
  const walletFilter =
    selectedWalletId === 'all' || !selectedType || selectedType === 'shared'
      ? null
      : selectedWalletId;

  const filtered = useMemo(() => {
    const { start, end } = getMonthBounds(month.getFullYear(), month.getMonth());
    return filterTransactions(transactions, {
      walletId: walletFilter,
      monthStart: start,
      monthEnd: end,
      searchQuery,
    });
  }, [transactions, walletFilter, month, searchQuery]);

  const summary = useMemo(() => computeMonthSummary(filtered, walletFilter), [filtered, walletFilter]);

  return (
    <div className="space-y-5">
      <WalletScroller />

      <SearchBar onChange={setSearchQuery} />

      <MonthNavigator month={month} onChange={setMonth} summary={summary} />

      {loading && transactions.length === 0 ? (
        <div className="space-y-3 animate-pulse">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-16 rounded-xl bg-slate-200/60 dark:bg-slate-800/60" />
          ))}
        </div>
      ) : transactions.length === 0 ? (
        <div className="text-center py-10 px-4">
          <Icons.Receipt size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
          <p className="text-slate-600 dark:text-slate-300 font-semibold mb-1">
            Brak transakcji
          </p>
          <p className="text-sm text-slate-400 dark:text-slate-500 mb-4">
            Dodaj pierwszą — szybkie kliknięcie <Icons.Plus size={14} className="inline align-middle" /> u dołu
          </p>
          <button
            type="button"
            onClick={() =>
              document.querySelector<HTMLButtonElement>('button[aria-label="Dodaj transakcję"]')?.click()
            }
            className="px-4 py-2.5 rounded-full bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold shadow-card transition"
          >
            Dodaj transakcję
          </button>
        </div>
      ) : (
        <TransactionList
          transactions={filtered}
          onPick={(tx) => useTxModal.getState().openEdit(tx)}
        />
      )}
    </div>
  );
}
